import { IRentedPropertyDetail } from "../types/ownerDashboard.types";
import { normalizeRentedPropertyPhotos } from "./normalizeRentedPropertyPhotos";

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function asNullableString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function asFiniteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeRentedPropertyDetail(
  value: unknown
): IRentedPropertyDetail | null {
  const record = asRecord(value);
  if (!record) {
    return null;
  }

  const propertyId = asFiniteNumber(record.property_id);
  if (propertyId == null) {
    return null;
  }

  const areaNumber = asFiniteNumber(record.area);
  const areaString = asNullableString(record.area);

  const listingPropertyId = asFiniteNumber(record.listing_property_id);

  return {
    property_id: propertyId,
    listing_property_id:
      listingPropertyId != null && listingPropertyId > 0
        ? listingPropertyId
        : null,
    kenlo_property_id: asNullableString(record.kenlo_property_id),
    title: asNullableString(record.title),
    address: asNullableString(record.address),
    description: asNullableString(record.description),
    area: areaNumber ?? areaString,
    apartment_number: asNullableString(record.apartment_number),
    address_floor: asNullableString(record.address_floor),
    address_complement: asNullableString(record.address_complement),
    unit: asNullableString(record.unit),
    floor: asNullableString(record.floor),
    complement: asNullableString(record.complement),
    local_data_available: record.local_data_available === true,
    photos: normalizeRentedPropertyPhotos(
      record.photos ?? record.listing_photos ?? record.property_photos
    ),
    is_vacating: record.is_vacating === true,
    expected_vacate_date: asNullableString(record.expected_vacate_date),
  };
}

export function normalizeRentedPropertiesDetails(
  details: unknown
): IRentedPropertyDetail[] {
  if (!Array.isArray(details)) {
    return [];
  }

  return details
    .map((item) => normalizeRentedPropertyDetail(item))
    .filter((item): item is IRentedPropertyDetail => item != null);
}
